/**
 * NotificationDropdown — 벨 클릭 시 알림 드롭다운 목록
 */
import { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useNotification } from "../../contexts/NotificationContext";
import { getNotifAvatar, getNotifBoardKey, fmtNotifTime, routeNotificationClick } from "../../utils/notificationCard";
import { shouldIgnoreClick } from "../../utils/clickGuard";

export default function NotificationDropdown({ onClose, onOpenPopup }: { onClose: () => void; onOpenPopup?: (key: string) => void }) {
  const { t } = useTranslation();
  const { notifications, markAsRead } = useNotification();
  const ref = useRef<HTMLDivElement>(null);

  // 읽지 않은 수신 메시지만, 최근 5개
  const unreadList = notifications.filter((n) => !n.read).slice(0, 5);

  useEffect(() => {
    // 요구사항 #467: 종 버튼도 토글로 닫히게. 종 버튼 클릭은 mousedown 시점에 이 outside-click
    // 이 먼저 닫아버리고 이어지는 버튼 자체의 onClick(toggle)이 다시 열어버려 "안 닫히는 것처럼"
    // 보이던 문제 — 종 버튼(data-notif-bell) 클릭은 이 핸들러에서 제외해 버튼 자신의 토글에 맡긴다.
    const handle = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (ref.current && !ref.current.contains(target) && !target.closest?.("[data-notif-bell]")) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handle);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const handleClick = (n: typeof notifications[0]) => {
    if (shouldIgnoreClick()) { onClose(); return; }
    // 즉시 read 처리 → unreadList filter 가 자동 제외 → 알림 목록에서 즉시 제거 효과
    markAsRead(n.id);
    onClose();
    routeNotificationClick(n, onOpenPopup);
  };

  return (
    <div ref={ref} className="fixed right-4 z-[200] w-80 max-w-[calc(100vw-32px)] max-h-96 overflow-y-auto rounded-xl bg-white border border-slate-200 shadow-2xl shadow-black/10"
      style={{ top: (document.querySelector("header")?.getBoundingClientRect().bottom || 60) + 4 }}
    >
      <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white rounded-t-xl z-10">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-700">{t("notif.title")}</span>
          {unreadList.length > 0 && (
            <span className="rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-bold text-amber-500">{unreadList.length}</span>
          )}
        </div>
        <button
          onClick={onClose}
          className="flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          aria-label="close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {unreadList.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-slate-400">{t("notif.empty")}</div>
      ) : (
        <div>
          {unreadList.map((n) => {
            const { avatarLabel, avatarBg } = getNotifAvatar(n);
            const boardKey = getNotifBoardKey(n);
            const boardName = boardKey ? t(`notif.board_${boardKey}`, { defaultValue: boardKey }) : "";
            return (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                className="w-full px-3 py-2.5 flex items-start gap-2.5 hover:bg-slate-50 transition-colors text-left"
              >
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-black mt-0.5 ${avatarBg}`}>
                  {avatarLabel}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {boardName && <span className="text-[10px] font-bold text-sky-600 shrink-0">{boardName}</span>}
                    {n.nickName && <span className="text-[10px] text-slate-500 truncate">· {n.nickName}</span>}
                  </div>
                  {/* 게시글이 있는 타입은 게시글 제목, 없는 타입(1:1문의/배너댓글 등)은 댓글 내용 */}
                  <div className="text-[11px] text-slate-700 truncate">{n.postTitle || n.body}</div>
                </div>
                <span className="text-[10px] text-slate-400 shrink-0 mt-0.5">{fmtNotifTime(n.timestamp)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
